import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { createColumnHelper } from '@tanstack/react-table';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import type { QmsRegistryKind } from '../features/qms/qmsApi';
import { fetchQmsRecordDetail, patchContingencyDone, patchCorrectiveImplemented } from '../features/qms/qmsApi';
import { permissionKeyForKind } from '../features/qms/permissionKeys';
import {
  labelPlanType,
  labelQmsActionStatus,
  qmsActionStatusBadgeClass,
  resolveCorrectiveDecisionSummary,
  isCorrectiveDecisionActive,
  type QmsOpeningDecisionInput,
} from '../features/qms/qmsLabels';
import {
  ContingencySection,
  CorrectiveActionsSection,
  CorrectiveDecisionSection,
  ExternalCommSection,
  FollowUpSection,
  ReviewSection,
  RootCauseAnalysisSection,
  StatusSummaryHeader,
  VerificationSection,
} from '../features/qms/QmsWorkflowPanels';
import { DataTable } from '../components/DataTable';
import { DateCell } from '../components/DateCell';
import { selectCurrentUser } from '../features/auth/authSlice';

type PlanRow = {
  id: string;
  planType: 'contingency' | 'corrective';
  what: string;
  status: string;
  dueAt: string;
};

const col = createColumnHelper<PlanRow>();

interface Props {
  kind: QmsRegistryKind;
  title: string;
  basePath: string;
}

function AssignedContingencyCard({
  action,
  onRefresh,
  onMsg,
}: {
  action: any;
  onRefresh: () => Promise<void>;
  onMsg: (s: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState('');

  return (
    <div className="rounded-md border border-border p-3 text-sm space-y-1">
      <p className="text-xs font-medium text-muted-foreground">Contingencia</p>
      <p className="font-medium">{action.what}</p>
      <p className="text-muted-foreground">Vence: <DateCell value={action.dueAt} /></p>
      <p className="text-muted-foreground">Estado: {labelQmsActionStatus(action.status)}</p>
      {action.status === 'pending' && !open && (
        <button
          type="button"
          className="mt-2 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground"
          onClick={() => setOpen(true)}
        >
          Cerrar contingencia
        </button>
      )}
      {action.status === 'pending' && open && (
        <div className="mt-2 space-y-2">
          <label className="text-xs text-muted-foreground">Nota de cierre (obligatoria)</label>
          <textarea
            className="w-full min-h-[56px] border rounded px-2 py-1 text-xs"
            placeholder="Describa qué se hizo y el resultado"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!notes.trim()}
              className="rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground disabled:opacity-50"
              onClick={async () => {
                await patchContingencyDone(action.id, notes.trim());
                await onRefresh();
                onMsg('Contingencia cerrada');
              }}
            >
              Confirmar cierre
            </button>
            <button type="button" className="text-xs text-muted-foreground" onClick={() => setOpen(false)}>
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AssignedActionsSection({
  opening,
  userId,
  onRefresh,
  onMsg,
}: {
  opening: Record<string, any>;
  userId: number;
  onRefresh: () => Promise<void>;
  onMsg: (s: string) => void;
}) {
  const assignedContingencies = (opening.contingencyActions ?? []).filter(
    (action: any) => action.assignedUserId === userId
  );
  const assignedCorrectives = (opening.correctiveActions ?? []).filter(
    (action: any) => action.assignedUserId === userId
  );
  const hasAssigned = assignedContingencies.length > 0 || assignedCorrectives.length > 0;

  if (!hasAssigned) return null;

  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-3">
      <div>
        <h2 className="text-lg font-medium">Mis acciones asignadas</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Podés consultar el expediente y marcar como completadas solo las acciones que te fueron asignadas.
        </p>
      </div>

      {assignedContingencies.map((action: any) => (
        <AssignedContingencyCard key={action.id} action={action} onRefresh={onRefresh} onMsg={onMsg} />
      ))}

      {assignedCorrectives.map((action: any) => (
        <div key={action.id} className="rounded-md border border-border p-3 text-sm space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Acción correctiva</p>
          <p className="font-medium">{action.what}</p>
          <p className="text-muted-foreground">Vence: <DateCell value={action.dueAt} /></p>
          <p className="text-muted-foreground">Estado: {labelQmsActionStatus(action.status)}</p>
          {!action.implementedAt && action.status !== 'cancelled' && (
            <button
              type="button"
              className="mt-2 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground"
              onClick={async () => {
                await patchCorrectiveImplemented(action.id);
                await onRefresh();
                onMsg('Acción correctiva completada');
              }}
            >
              Marcar como completada
            </button>
          )}
        </div>
      ))}
    </section>
  );
}

export function QmsActionPlansPage({ kind, title, basePath }: Props) {
  const { id: openingId } = useParams<{ id: string }>();
  const { canEdit, ready } = usePlatformAccessLevel(permissionKeyForKind(kind));
  const canManage = canEdit && ready;
  const currentUser = useSelector(selectCurrentUser);
  const [opening, setOpening] = useState<Record<string, any> | null>(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    if (!openingId) return;
    try {
      const d = await fetchQmsRecordDetail(openingId);
      setOpening(d as Record<string, any>);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error');
    }
  }, [openingId]);

  useEffect(() => {
    load();
  }, [load]);

  const planRows: PlanRow[] = useMemo(() => {
    if (!opening || opening.scope === 'own_minimal') return [];
    const rows: PlanRow[] = [];
    for (const c of opening.contingencyActions ?? []) {
      rows.push({
        id: c.id,
        planType: 'contingency',
        what: c.what,
        status: c.status,
        dueAt: c.dueAt,
      });
    }
    for (const c of opening.correctiveActions ?? []) {
      rows.push({
        id: c.id,
        planType: 'corrective',
        what: c.what,
        status: c.status,
        dueAt: c.dueAt,
      });
    }
    return rows;
  }, [opening]);

  const columns = useMemo(
    () => [
      col.accessor('planType', {
        header: 'Tipo',
        cell: (info) => labelPlanType(info.getValue()),
      }),
      col.accessor('what', {
        header: 'Descripción',
        cell: (info) => <span className="line-clamp-2 max-w-md">{info.getValue()}</span>,
      }),
      col.accessor('status', {
        header: 'Estado',
        cell: (info) => (
          <span className={qmsActionStatusBadgeClass(info.getValue())}>{labelQmsActionStatus(info.getValue())}</span>
        ),
      }),
      col.accessor('dueAt', {
        header: 'Vencimiento / plazo',
        cell: (info) => <DateCell value={info.getValue()} />,
      }),
    ],
    []
  );

  if (error) {
    return (
      <div className="container max-w-4xl py-8 px-4">
        <p className="text-destructive">{error}</p>
        <Link to={basePath} className="mt-4 inline-block text-primary">
          Volver al listado
        </Link>
      </div>
    );
  }

  if (!opening) {
    return (
      <div className="container max-w-4xl py-8 px-4">
        <p className="text-muted-foreground">Cargando…</p>
      </div>
    );
  }

  if (opening.scope === 'own_minimal') {
    return (
      <div className="container max-w-4xl py-8 px-4">
        <p className="text-muted-foreground">No tiene acceso a los planes de acción de este registro.</p>
        <Link to={basePath} className="mt-4 inline-block text-primary">
          Volver
        </Link>
      </div>
    );
  }

  const workflowClosed = ['dismissed', 'closed_simple', 'closed_corrective'].includes(opening.status);
  const dismissed = opening.reviewOutcome === 'dismissed' || opening.status === 'dismissed';
  const reviewPertinent = opening.reviewOutcome === 'pertinent';

  const contingencyBlocked =
    workflowClosed || dismissed
      ? null
      : !reviewPertinent
        ? 'En el paso 1 indique si el caso es pertinente. Solo entonces podrá registrar la contingencia.'
        : null;

  const verifyBlocked =
    workflowClosed || dismissed
      ? null
      : opening.status === 'contingency_executed'
        ? null
        : ['corrective_decision_pending', 'contingency_verified', 'corrective_in_progress', 'closed_simple', 'closed_corrective'].includes(
              opening.status
            )
          ? 'La verificación de contingencia ya está registrada. Siga con el paso 4.'
          : 'Cuando todas las acciones de contingencia estén hechas, podrá verificar si el desvío se eliminó.';

  const correctiveDecisionBlocked =
    workflowClosed || dismissed || resolveCorrectiveDecisionSummary(opening as QmsOpeningDecisionInput)
      ? null
      : !reviewPertinent
        ? 'Complete el paso 1 (revisión) antes de decidir si aplica una acción correctiva.'
        : isCorrectiveDecisionActive(opening.status)
          ? null
          : 'Verifique la contingencia en el paso 3 antes de decidir si aplica una acción correctiva.';

  const rootCauseBlocked =
    workflowClosed || dismissed
      ? null
      : opening.status !== 'corrective_in_progress'
        ? 'Indique en el paso 4 si aplica una acción correctiva. Si corresponde, podrá registrar el análisis de causa raíz aquí.'
        : null;

  const correctiveBlocked =
    workflowClosed || dismissed
      ? null
      : opening.status !== 'corrective_in_progress'
        ? 'Indique en el paso 4 si aplica una acción correctiva. Si corresponde, podrá cargar el plan aquí.'
        : null;

  const followUpBlocked =
    workflowClosed || dismissed ? 'No se puede añadir seguimiento: expediente cerrado o desestimado.' : !reviewPertinent ? 'Complete primero el paso 1 (revisión).' : null;

  const reviewHeading =
    kind === 'non_conformity' ? '1. Revisión de la no conformidad' : '1. Revisión del caso';
  const originGuidance =
    opening.originType === 'external'
      ? 'Origen externo: la acción correctiva queda prevista por defecto. Si no corresponde tomarla, se debe justificar.'
      : 'Origen interno: la acción correctiva es opcional y se decide después de verificar la contingencia.';

  return (
    <div className="container max-w-4xl py-8 px-4 space-y-8">
      <div className="flex flex-wrap justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Planes de acción</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {title} — registro {openingId}
          </p>
        </div>
        <div className="flex gap-3">
          <Link to={`${basePath}/${openingId}`} className="text-sm text-primary hover:underline self-center">
            Ver detalle completo
          </Link>
          <Link to={basePath} className="text-sm text-muted-foreground hover:underline self-center">
            Listado
          </Link>
        </div>
      </div>

      {msg && <p className="text-sm text-green-600 dark:text-green-400">{msg}</p>}

      <StatusSummaryHeader
        openingId={opening.id}
        statusSummary={opening.statusSummary}
        canManage={canManage}
        onRefresh={load}
        onMsg={setMsg}
      />

      <section>
        <h2 className="text-lg font-medium mb-3">Listado de planes</h2>
        <p className="text-xs text-muted-foreground mb-4">
          Contingencias y acciones correctivas vinculadas a esta no conformidad o reclamo.
        </p>
        <DataTable columns={columns} data={planRows} emptyMessage="No hay planes registrados" pageSizeOptions={[10, 20, 50]} />
      </section>

      {!canManage && currentUser && (
        <AssignedActionsSection
          opening={opening}
          userId={currentUser.id}
          onRefresh={load}
          onMsg={setMsg}
        />
      )}

      {canManage && (
        <>
          {kind === 'non_conformity' && !workflowClosed && (
            <section className="rounded-lg border border-border bg-muted/30 p-4 text-sm space-y-2">
              <h2 className="font-medium text-base">Pasos del expediente</h2>
              <p className="text-muted-foreground">{originGuidance}</p>
              <ol className="list-decimal pl-5 space-y-1.5 text-muted-foreground">
                <li>Decidir si la no conformidad es pertinente (si no, queda archivada sin estadística).</li>
                <li>Definir la contingencia: medida rápida (qué, quién, cuándo, recursos).</li>
                <li>Verificar que la contingencia eliminó el desvío.</li>
                <li>Decidir si hace falta acción correctiva (análisis profundo; plazos más largos).</li>
                <li>Documentar el análisis de causa raíz: causa detectada, herramienta utilizada y participantes.</li>
                <li>Registrar el plan de acción correctiva, seguimiento y eficacia antes del cierre con AC.</li>
              </ol>
            </section>
          )}

          <ReviewSection
            openingId={opening.id}
            heading={reviewHeading}
            reviewOutcome={opening.reviewOutcome ?? 'pending'}
            recordStatus={opening.status}
            dismissReason={opening.dismissReason}
            onDone={async () => {
              await load();
              setMsg('Revisión guardada');
            }}
          />

          {reviewPertinent && !dismissed && (
            <>
              {!workflowClosed && (
                <>
                  <ContingencySection
                    opening={opening}
                    onRefresh={load}
                    onMsg={setMsg}
                    blockedMessage={contingencyBlocked}
                  />

                  <VerificationSection
                    opening={opening}
                    onRefresh={load}
                    onMsg={setMsg}
                    blockedMessage={verifyBlocked}
                  />
                </>
              )}

              <CorrectiveDecisionSection
                opening={opening}
                onRefresh={load}
                onMsg={setMsg}
                blockedMessage={correctiveDecisionBlocked}
              />

              {!workflowClosed && (
                <>
                  <RootCauseAnalysisSection
                    opening={opening}
                    onRefresh={load}
                    onMsg={setMsg}
                    blockedMessage={rootCauseBlocked}
                  />

                  <CorrectiveActionsSection
                    opening={opening}
                    onRefresh={load}
                    onMsg={setMsg}
                    blockedMessage={correctiveBlocked}
                  />

                  <FollowUpSection
                    opening={opening}
                    onRefresh={load}
                    onMsg={setMsg}
                    blockedMessage={followUpBlocked}
                  />
                </>
              )}
            </>
          )}

          {opening.originType === 'external' &&
            (opening.attributability === 'supplier' || opening.attributability === 'external_carrier') &&
            !workflowClosed &&
            !dismissed && (
              <ExternalCommSection openingId={opening.id} onMsg={setMsg} />
            )}
        </>
      )}
    </div>
  );
}
